import { useMemo, useEffect } from 'react';
import { useUIStore, useSettingsStore, useAuthStore } from '@/stores';
import type { ItemType } from '@perfin/shared';

const selectClassName = "rounded-lg border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 min-w-[100px]";

const priorityOptions = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'future', label: 'Future' },
];

const itemTypeOptions: { value: ItemType; label: string }[] = [
  { value: 'task', label: 'Tasks' },
  { value: 'transaction', label: 'Expenses' },
  { value: 'note', label: 'Notes' },
  { value: 'keyDate', label: 'Key Dates' },
];

const PAGE_KEY = 'notifications' as const;

export function NotificationsFilterBar() {
  const { user } = useAuthStore();
  const {
    categoryFilters,
    setCategoryFilter,
    setSubCategoryFilter,
    clearCategoryFilters,
    pageFilters,
    setOwnerFilter,
    setStatusFilter,
    setPriorityFilter,
    setItemGroupFilter,
  } = useUIStore();
  const { members, categories } = useSettingsStore();

  const filter = categoryFilters?.[PAGE_KEY] ?? { categoryId: null, subCategoryId: null };
  const pFilters = pageFilters?.[PAGE_KEY] ?? { ownerFilter: null, itemGroupFilter: null, statusFilter: null, priorityFilter: null };

  // Default owner to signed-in user on first load only (null = never set, '' = explicitly "All Owners")
  useEffect(() => {
    if (user?.uid && pFilters.ownerFilter === null) {
      setOwnerFilter(PAGE_KEY, user.uid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Owner options
  const ownerOptions = useMemo(() => {
    return [
      { value: '', label: 'All Owners' },
      ...members.map(m => ({
        value: m.userId,
        label: m.displayName || m.email || m.userId
      }))
    ];
  }, [members]);

  // itemGroupFilter is repurposed as itemType filter for notifications
  const selectedItemType = pFilters.itemGroupFilter as ItemType | null;

  // Categories — show all categories, or filtered by selected item type
  const availableCategories = useMemo(() => {
    if (selectedItemType) {
      return categories.filter(cat => cat.itemTypes.includes(selectedItemType));
    }
    return categories;
  }, [categories, selectedItemType]);

  // Subcategories for selected category
  const selectedCategory = availableCategories.find((cat) => cat.id === filter.categoryId);
  const subCategories = selectedCategory?.subCategories || [];

  const hasActiveFilters =
    (pFilters.ownerFilter !== null && pFilters.ownerFilter !== '' && pFilters.ownerFilter !== user?.uid) ||
    pFilters.itemGroupFilter !== null ||
    filter.categoryId !== null ||
    filter.subCategoryId !== null ||
    pFilters.statusFilter !== null ||
    pFilters.priorityFilter !== null;

  const clearAllFilters = () => {
    setOwnerFilter(PAGE_KEY, user?.uid || '');
    setItemGroupFilter(PAGE_KEY, null);
    setCategoryFilter(PAGE_KEY, null);
    setStatusFilter(PAGE_KEY, null);
    setPriorityFilter(PAGE_KEY, null);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Owner dropdown */}
      <select
        value={pFilters.ownerFilter || ''}
        onChange={(e) => setOwnerFilter(PAGE_KEY, e.target.value)}
        className={selectClassName}
      >
        {ownerOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Item Type dropdown (stored in itemGroupFilter) */}
      <select
        value={pFilters.itemGroupFilter || ''}
        onChange={(e) => {
          setItemGroupFilter(PAGE_KEY, e.target.value || null);
          // Reset category when item type changes (use clearCategoryFilters to avoid cascade that wipes itemGroupFilter)
          clearCategoryFilters(PAGE_KEY);
        }}
        className={selectClassName}
      >
        <option value="">All Types</option>
        {itemTypeOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Category dropdown */}
      <select
        value={filter.categoryId || ''}
        onChange={(e) => setCategoryFilter(PAGE_KEY, e.target.value || null)}
        className={selectClassName}
      >
        <option value="">All Categories</option>
        {availableCategories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </select>

      {/* Subcategory dropdown */}
      <select
        value={filter.subCategoryId || ''}
        onChange={(e) => setSubCategoryFilter(PAGE_KEY, e.target.value || null)}
        disabled={!filter.categoryId || subCategories.length === 0}
        className={selectClassName}
        style={!filter.categoryId || subCategories.length === 0 ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#9ca3af' } : {}}
      >
        <option value="">All Subcategories</option>
        {subCategories.map((sub) => (
          <option key={sub.id} value={sub.id}>
            {sub.name}
          </option>
        ))}
      </select>

      {/* Priority Filter */}
      <select
        value={pFilters.priorityFilter || ''}
        onChange={(e) => setPriorityFilter(PAGE_KEY, e.target.value || null)}
        className={selectClassName}
      >
        <option value="">All Priority</option>
        {priorityOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Status Filter */}
      <select
        value={pFilters.statusFilter || ''}
        onChange={(e) => setStatusFilter(PAGE_KEY, e.target.value || null)}
        className={selectClassName}
      >
        <option value="">All Status</option>
        <option value="new">New</option>
        <option value="inProgress">In Progress</option>
        <option value="complete">Complete</option>
        <option value="draft">Draft</option>
        <option value="pending">Pending</option>
        <option value="final">Final</option>
      </select>

      {/* Clear button */}
      {hasActiveFilters && (
        <button
          onClick={clearAllFilters}
          className="px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
        >
          Clear
        </button>
      )}
    </div>
  );
}
